import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { questions, categories } from "./questions";
import { useToken } from "./TokenContext";
import "../css/questionnaire.css";
import guaidSound from "../voice/guaid.wav";
import waitSound from "../voice/wait.wav";

function Questionnaire() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [interestResponses, setInterestResponses] = useState({});
  const [personalityResponses, setPersonalityResponses] = useState({});
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("データ送信中...");
  const { loggedInUser } = useToken();
  const navigate = useNavigate();

  const SERVER_URL = "https://mpzzlvohx7.execute-api.ap-northeast-3.amazonaws.com/prod/api/questionnaire";
  const SERVER_URL_PERSONAL = "https://mpzzlvohx7.execute-api.ap-northeast-3.amazonaws.com/prod/api/big-five";

  const PERSONALITY_KEYS = ["誠実性", "協調性", "情緒安定性", "外向性", "開放性"];

  useEffect(() => {
    const preserveKeys = [
      "userName",
      "companyName_findjob",
      "userAddress",
      "loggedInToken",
      "grade",
    ];
    Object.keys(localStorage).forEach((key) => {
      if (!preserveKeys.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    const storedName = localStorage.getItem("userName");
    if (storedName) setName(storedName);
    const audio = new Audio(guaidSound);
    audio.play().catch((error) => {
      console.error("自動再生に失敗しました:", error);
    });
  }, []);

  useEffect(() => {
    if (isSubmitting) {
      const audio = new Audio(waitSound);
      audio.play().catch((error) => {
        console.error("wait.wav の自動再生に失敗しました:", error);
      });
    }
  }, [isSubmitting]);

  const handleAnswer = (score) => {
    const currentQuestion = questions[currentQuestionIndex];
    const newInterest = { ...interestResponses };
    const newPersonality = { ...personalityResponses };

    currentQuestion.categories.forEach((category) => {
      if (PERSONALITY_KEYS.includes(category)) {
        newPersonality[category] = (newPersonality[category] || 0) + score;
      } else {
        newInterest[category] = (newInterest[category] || 0) + score;
      }
    });

    setInterestResponses(newInterest);
    setPersonalityResponses(newPersonality);

    if (currentQuestionIndex === questions.length - 1) {
      setIsSubmitting(true);
      sendResults(newInterest, newPersonality);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const calculateMaxScores = (keys) => {
    const maxScores = {};
    keys.forEach((key) => {
      maxScores[key] = questions.filter((q) =>
        q.categories.includes(key)
      ).length * 5;
    });
    return maxScores;
  };

  const calculateNormalizedScores = (responses, maxScores) => {
    return Object.fromEntries(
      Object.entries(responses).map(([category, score]) => [
        category,
        Math.round((score / maxScores[category]) * 100 * 100) / 100,
      ])
    );
  };

  const sendResults = async (interest, personality) => {
    const interestMax = calculateMaxScores(categories.filter((c) => !PERSONALITY_KEYS.includes(c)));
    const personalityMax = calculateMaxScores(PERSONALITY_KEYS);

    const normalizedInterest = calculateNormalizedScores(interest, interestMax);
    const normalizedPersonality = calculateNormalizedScores(personality, personalityMax);

    const timestamp = new Date().toISOString();

    const payload = {
      name,
      normalized_scores: normalizedInterest,
      affilication: loggedInUser,
      timestamp,
      token: localStorage.getItem("loggedInToken")
    };

    const personalityPayload = {
      name,
      affilication: loggedInUser,
      timestamp,
      personality_traits: normalizedPersonality,
      token: localStorage.getItem("loggedInToken")  // ✅ 追加！
    };    

    //console.log("職業適性スコア:", normalizedInterest);
    //console.log("性格スコア:", normalizedPersonality);

    try {
      // ✅ 性格診断：OPTIONSリクエスト対応、CORSヘッダーのチェック
      // big-five APIのエラーは警告として記録するが、処理は続行する
      try {
        await axios.post(SERVER_URL_PERSONAL, personalityPayload, {
          headers: { "Content-Type": "application/json" },
          withCredentials: false
        });
      } catch (personalityError) {
        console.warn("性格診断APIの送信に失敗しました（処理は続行します）:", personalityError);
        // エラーが発生してもquestionnaire APIの処理は続行する
      }

      const response = await axios.post(SERVER_URL, payload, {
        headers: { "Content-Type": "application/json" },
      });

      setProgress((prevProgress) => Math.min(prevProgress + 20, 100));

      const { progress_updates, recommendations, pdf_url, message } = response.data;

      // pdf_urlが存在しない場合の警告
      if (!pdf_url) {
        console.warn("PDF URLがレスポンスに含まれていません");
      }

      for (const update of progress_updates) {
        const step = Math.max(5, Math.floor((update.progress - progress) / 5));
        for (let p = progress; p < update.progress; p += step) {
          setProgress(p);
          await new Promise((resolve) => setTimeout(resolve, 30));
        }
        setProgress(update.progress); // 最終値に調整
        setStatus(update.status);
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      navigate("/result", {
        state: {
          recommendations,
          pdf_url,
          message,
          normalizedScores: normalizedInterest,
        }
      });
    } catch (error) {
      console.error("送信エラー:", error);
      const errorMessage = error.response?.data?.message || error.message || "送信中にエラーが発生しました。";
      alert(`送信中にエラーが発生しました: ${errorMessage}`);
      setIsSubmitting(false);
      setProgress(0);
    }
  };

  useEffect(() => {
    document.body.style.cursor = isSubmitting ? "wait" : "default";
    return () => {
      document.body.style.cursor = "default";
    };
  }, [isSubmitting]);

  return (
    <div className={`questionnaire-container ${isSubmitting ? "waiting" : ""}`}>
      <h1>こんにちは、{name}さん</h1>
      {isSubmitting ? (
        <div className="loading-container">
          <p className="status-text">{status}</p>
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
      ) : (
        <>
          <h2>
            質問 {currentQuestionIndex + 1} / {questions.length}
          </h2>
          <p>{questions[currentQuestionIndex].text}</p>
          <p>1:まったく当てはまらない～5:すごく当てはまる</p>
          <div className="answer-group">
            {[1, 2, 3, 4, 5].map((score) => (
              <button
                key={score}
                onClick={() => handleAnswer(score)}
                className="answer-button"
                disabled={isSubmitting}
              >
                {score}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default Questionnaire;
